import { cva, type VariantProps } from "class-variance-authority";
import { useMemo } from "react";

const numberVariants = cva("rounded-full text-white", {
  variants: {
    size: {
      xs: "px-2 py-0 text-sm font-small",
      sm: "px-3 py-1 text-sm font-medium",
      md: "px-5 py-2 text-md font-medium",
      lg: "px-8 py-3 text-lg font-medium",
    },
  },
  defaultVariants: {
    size: "sm",
  },
});

export interface MetricColorNumberProps
  extends VariantProps<typeof numberVariants> {
  value: number;
}

export function MetricColorNumber({ size, value }: MetricColorNumberProps) {
  const getHue = useMemo(() => {
    // Ensure value is within 0-100 range
    const clampedValue = Math.max(0, Math.min(100, value));

    // Define color stops for hue
    if (clampedValue <= 50) {
      // Interpolate between red (0) and yellow (50)
      const ratio = clampedValue / 50;
      return 0 + 60 * ratio; // 0 (red) to 60 (yellow)
    } else {
      // Interpolate between yellow (50) and green (100)
      const ratio = (clampedValue - 50) / 50;
      return 60 + 60 * ratio; // 60 (yellow) to 120 (green)
    }
  }, [value]);

  return (
    <span
      className={`${numberVariants({ size })} metric-color-badge transition-colors duration-200 shadow-sm`}
      style={
        {
          "--metric-hue": getHue,
        } as React.CSSProperties & { "--metric-hue": number }
      }
    >
      {value}
    </span>
  );
}
